import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Album {
  id: number;
  category_id: number;
  title: string;
  description: string | null;
  cover_image: string | null;
  sort_order: number;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface SortableAlbumItemProps {
  album: Album;
  isSelected: boolean;
  onSelect: (album: Album) => void;
  onDelete: (id: number) => void;
  categories: Category[];
  onMoveCategory: (albumId: number, newCategoryId: number) => void;
}

function SortableAlbumItem({
  album,
  isSelected,
  onSelect,
  onDelete,
  categories,
  onMoveCategory,
}: SortableAlbumItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: album.id });

  const [showMoveMenu, setShowMoveMenu] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto' as const,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
        isSelected
          ? 'bg-dark text-white'
          : 'bg-white text-dark hover:bg-warm-100'
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className={`w-6 h-6 flex items-center justify-center rounded cursor-grab active:cursor-grabbing flex-shrink-0 ${
          isSelected ? 'text-white/60 hover:text-white' : 'text-dark/30 hover:text-dark/60'
        }`}
        onClick={(e) => e.stopPropagation()}
        title="Arraste para reordenar"
      >
        <i className="ri-drag-move-2-line text-sm" />
      </button>

      <div
        className="flex-1 min-w-0 flex items-center gap-2 cursor-pointer"
        onClick={() => onSelect(album)}
      >
        {album.cover_image && (
          <img src={album.cover_image} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />
        )}
        <span className="truncate select-none">{album.title}</span>
      </div>

      <div className="relative flex items-center gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMoveMenu((prev) => !prev);
          }}
          className={`w-6 h-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity ${
            isSelected ? 'hover:bg-white/20' : 'hover:bg-warm-200 text-dark/50'
          }`}
          title="Mover para outra categoria"
        >
          <i className="ri-folder-transfer-line text-xs" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(album.id);
          }}
          className={`w-6 h-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity ${
            isSelected ? 'hover:bg-white/20 text-red-300' : 'hover:bg-red-50 text-red-500'
          }`}
          title="Excluir álbum"
        >
          <i className="ri-delete-bin-line text-xs" />
        </button>

        {showMoveMenu && (
          <div className="absolute right-0 top-7 z-50 bg-white rounded-lg border border-warm-200 shadow-lg p-2 w-48">
            <p className="text-[10px] font-semibold text-dark/40 uppercase tracking-wider mb-1.5 px-1">
              Mover para
            </p>
            <div className="space-y-0.5 max-h-40 overflow-y-auto">
              {categories
                .filter((c) => c.id !== album.category_id)
                .map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      onMoveCategory(album.id, cat.id);
                      setShowMoveMenu(false);
                    }}
                    className="w-full text-left px-2 py-1.5 rounded text-xs text-dark hover:bg-warm-100 transition-colors"
                  >
                    {cat.name}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface Props {
  albums: Album[];
  selectedId: number | null;
  onSelect: (album: Album) => void;
  onDelete: (id: number) => void;
  onReorder: (albums: Album[]) => void;
  categories: Category[];
  onMoveCategory: (albumId: number, newCategoryId: number) => void;
}

export default function SortableAlbumList({
  albums,
  selectedId,
  onSelect,
  onDelete,
  onReorder,
  categories,
  onMoveCategory,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = albums.findIndex((a) => a.id === active.id);
    const newIndex = albums.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(albums, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={albums.map((a) => a.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
          {albums.map((album) => (
            <SortableAlbumItem
              key={album.id}
              album={album}
              isSelected={selectedId === album.id}
              onSelect={onSelect}
              onDelete={onDelete}
              categories={categories}
              onMoveCategory={onMoveCategory}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}